import {
    useDeleteContentReactionMutation,
    useInsertContentReactionMutation
} from "graphql/generated";
import { fireEvent } from "lib/ga";
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "./useAuth";
import { useContentDetailModal } from "./useContentDetailModal";

// TODO: Hasura computed field 설정 후 서버 상태 기반으로 전환
// 현재는 로컬 상태로 관리 (페이지 새로고침 시 초기화됨)
export const useContentReaction = (
    content: Pick<Mix_Content, "id" | "originalurl"> & {
        reacted?: boolean | null;
        reaction_count?: number | null;
    }
) => {
    const { isLoggedIn, openSignInModal } = useAuth();
    const { saveContentUrlForRedirect } = useContentDetailModal();

    // 로컬 상태로 관리 (Hasura computed field 설정 전까지)
    const [isReacted, setIsReacted] = useState(!!content.reacted);
    const [reactionCount, setReactionCount] = useState(
        content.reaction_count ?? 0
    );

    // content가 변경되면 상태 리셋 (관련 콘텐츠/인기 토론 클릭 시)
    useEffect(() => {
        setIsReacted(!!content.reacted);
        setReactionCount(content.reaction_count ?? 0);
    }, [content.id, content.reacted, content.reaction_count]);

    const [insertReaction, { loading: insertLoading }] =
        useInsertContentReactionMutation({
            onCompleted: () => {
                setIsReacted(true);
                setReactionCount((prev) => prev + 1);
                fireEvent({
                    event: "content_reaction",
                    params: {
                        content_id: content.id,
                        content_url: content.originalurl,
                        value: true
                    }
                });
            },
            onError: () => {
                // 이미 좋아요한 경우 상태만 업데이트
                setIsReacted(true);
            }
        });

    const [deleteReaction, { loading: deleteLoading }] =
        useDeleteContentReactionMutation({
            onCompleted: (data) => {
                if (data.delete_mix_content_reaction?.affected_rows) {
                    setIsReacted(false);
                    setReactionCount((prev) => Math.max(0, prev - 1));
                }
                fireEvent({
                    event: "content_reaction",
                    params: {
                        content_id: content.id,
                        content_url: content.originalurl,
                        value: false
                    }
                });
            }
        });

    const toggle = useCallback(() => {
        if (isLoggedIn) {
            if (!isReacted) {
                insertReaction({
                    variables: {
                        contentId: content.id
                    }
                });
            } else {
                deleteReaction({
                    variables: {
                        contentId: content.id
                    }
                });
            }
        } else {
            // 로그인 후 콘텐츠 모달로 돌아오기 위해 URL 저장
            saveContentUrlForRedirect();
            openSignInModal();
        }
    }, [
        content.id,
        isReacted,
        isLoggedIn,
        insertReaction,
        deleteReaction,
        openSignInModal,
        saveContentUrlForRedirect
    ]);

    return {
        toggle,
        loading: insertLoading || deleteLoading,
        isReacted,
        reactionCount
    };
};
